import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { NAV, SITE, SOCIALS } from "../data/site";
import { WhatsAppIcon } from "./Header";
import { SOCIAL_ICONS } from "./ShareButtons";

const InstagramIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
);

const YouTubeIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
);

const LinkedInIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
);

const SOCIAL_ICON_MAP = {
    Instagram: InstagramIcon,
    Facebook: SOCIAL_ICONS.FacebookIcon,
    YouTube: YouTubeIcon,
    LinkedIn: LinkedInIcon,
};

const Footer = () => (
    <footer className="bg-navy text-slate-300 grain-overlay" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
            <div>
                <div className="flex items-center gap-2.5 mb-4">
                    <img src="/assets/om-infra-logo.jpg" alt="OM Infra logo — YEIDA plot advisory" title="OM Infra" width="40" height="40" className="w-10 h-10 rounded-sm object-cover bg-white" />
                    <span className="font-serif font-bold text-white text-lg">OM Infra</span>
                </div>
                <p className="text-sm leading-relaxed text-slate-400">
                    Yamuna Expressway's trusted advisory for verified YEIDA Authority plots and semi commercial (7% Kisan Kota / Farmer Quota)
                    plots near Noida International Airport, Jewar. Clear titles, transparent pricing, complete legal support.
                </p>
            </div>
            <div>
                <h3 className="text-white font-semibold text-sm uppercase tracking-[0.2em] mb-4">Quick Links</h3>
                <ul className="space-y-2.5 text-sm">
                    {NAV.flatMap((n) => (n.children ? [n, ...n.children] : [n])).filter((n) => n.to).slice(0, 12).map((n) => (
                        <li key={n.to + n.label}>
                            <Link to={n.to} className="hover:text-gold transition-colors" data-testid={`footer-link-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                {n.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
            <div>
                <h3 className="text-white font-semibold text-sm uppercase tracking-[0.2em] mb-4">Contact</h3>
                <ul className="space-y-3 text-sm">
                    <li className="flex gap-2.5"><MapPin className="w-4 h-4 text-gold shrink-0 mt-0.5" /><span>{SITE.address}</span></li>
                    <li>
                        <a href={SITE.phoneHref} className="flex gap-2.5 hover:text-gold transition-colors" data-testid="footer-phone">
                            <Phone className="w-4 h-4 text-gold shrink-0 mt-0.5" /> {SITE.phone}
                        </a>
                    </li>
                    <li>
                        <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" className="flex gap-2.5 hover:text-gold transition-colors" data-testid="footer-whatsapp">
                            <WhatsAppIcon className="w-4 h-4 text-gold shrink-0 mt-0.5" /> WhatsApp Us
                        </a>
                    </li>
                    <li>
                        <a href={`mailto:${SITE.email}`} className="flex gap-2.5 hover:text-gold transition-colors" data-testid="footer-email">
                            <Mail className="w-4 h-4 text-gold shrink-0 mt-0.5" /> {SITE.email}
                        </a>
                    </li>
                    <li className="flex gap-2.5"><Clock className="w-4 h-4 text-gold shrink-0 mt-0.5" /><span>{SITE.hours}</span></li>
                </ul>
                <div className="mt-5">
                    <h3 className="text-white font-semibold text-xs uppercase tracking-[0.2em] mb-3">Follow Us</h3>
                    <div className="flex gap-3" data-testid="footer-socials">
                        {SOCIALS.map((s) => {
                            const Icon = SOCIAL_ICON_MAP[s.label];
                            return (
                                <a
                                    key={s.label}
                                    href={s.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`OM Infra on ${s.label}`}
                                    data-testid={`social-${s.label.toLowerCase()}`}
                                    className="text-[#c9a227] hover:text-[#e6c250] transition-colors"
                                >
                                    <Icon className="w-6 h-6" />
                                </a>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div>
                <h3 className="text-white font-semibold text-sm uppercase tracking-[0.2em] mb-4">Disclaimer</h3>
                <p className="text-xs leading-relaxed text-slate-500">
                    OM Infra is a real estate advisory firm and is not the Yamuna Expressway Industrial Development Authority
                    (YEIDA). All plot information is subject to verification and change. RERA Agent Reg.: {SITE.rera}.
                    Prices mentioned are indicative market rates, not an offer to sell by the Authority.
                </p>
            </div>
        </div>
        <div className="border-t border-white/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
                <span data-testid="footer-copyright">© {new Date().getFullYear()} OM Infra. All rights reserved.</span>
                <span className="flex items-center gap-4">
                    <Link to="/privacy-policy/" className="hover:text-gold transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
                    <Link to="/terms-conditions/" className="hover:text-gold transition-colors" data-testid="footer-terms-link">Terms & Conditions</Link>
                </span>
                <span>YEIDA · Yamuna Expressway · Noida International Airport, Jewar</span>
            </div>
        </div>
    </footer>
);

export default Footer;
